// Contenu de la politique de confidentialité, traduit FR / EN / RU.
// Blocs : {t:'p'|'h3'|'note', x} · {t:'ul', x:[...]} · {t:'table', head:[...], rows:[[...]]}
// Le **gras** est rendu via <strong> par le composant.

export const privacyContent = {
  fr: {
    title: "Politique de confidentialité — rememor",
    updated: "Dernière mise à jour : 30 juin 2026",
    back: "Retour à rememor",
    intro: [
      "rememor est une application qui te permet de capturer tes souvenirs — à la voix ou par écrit — et de les retrouver au fil du temps. Tes souvenirs sont ce que tu as de plus personnel. Cette page explique, en clair, quelles données nous traitons, pourquoi, avec qui, et quels sont tes droits.",
      "Un principe guide tout le reste : **tes souvenirs t'appartiennent. Nous ne les vendons pas, nous ne les exploitons à aucune fin publicitaire, et nous ne les partageons avec personne en dehors des prestataires techniques strictement nécessaires au fonctionnement du service.**",
    ],
    sections: [
      {
        title: "1. Qui est responsable de tes données",
        blocks: [
          { t: "p", x: "Le responsable du traitement est :" },
          { t: "ul", x: ["**Eric Brisset**", "**Lettonie**", "Contact : **eric@0gbaltics.com**"] },
          { t: "p", x: "Pour toute question concernant tes données ou pour exercer tes droits, tu peux nous écrire à l'adresse ci-dessus." },
        ],
      },
      {
        title: "2. Quelles données nous traitons",
        blocks: [
          { t: "h3", x: "Données de compte" },
          { t: "ul", x: [
            "Ton **adresse e-mail** (sert à te connecter via un lien sécurisé).",
            "Ton **prénom** (pour personnaliser l'application).",
            "Ton **année de naissance** *(facultative — pour mieux situer tes souvenirs dans le temps)*.",
          ] },
          { t: "h3", x: "Tes souvenirs (le cœur du service)" },
          { t: "ul", x: [
            "Le **texte** de tes souvenirs (saisi ou issu de la transcription de ta voix).",
            "Les **enregistrements audio** que tu réalises.",
            "Les **photos** que tu choisis d'ajouter à un souvenir.",
          ] },
          { t: "h3", x: "Données générées par l'analyse automatique" },
          { t: "p", x: "Pour t'aider à retrouver et organiser tes souvenirs, ils sont analysés automatiquement, ce qui produit : une **émotion** dominante, des **thèmes** (période de vie, lieu, personnes évoquées…), une éventuelle **date** du souvenir, et une **représentation numérique** (« embedding ») servant à la recherche et aux rapprochements entre souvenirs." },
          { t: "h3", x: "Réponses facultatives" },
          { t: "ul", x: [
            "Si tu le souhaites, tes réponses au court questionnaire « Aide-nous à améliorer rememor » (habitudes, attentes…).",
            "Ton accord éventuel pour être recontacté à propos du service.",
          ] },
          { t: "h3", x: "Données techniques minimales" },
          { t: "ul", x: [
            "Informations de base nécessaires au fonctionnement (type d'appareil/navigateur, langue, horodatage des actions). Nous ne réalisons pas de profilage publicitaire.",
          ] },
          { t: "note", x: "**Important :** tu décides librement de ce que tu enregistres. Un souvenir peut contenir des informations sensibles te concernant ou concernant des tiers. Sois attentif à ce que tu confies à l'application, et n'enregistre des informations sur d'autres personnes que dans le respect de leur vie privée." },
        ],
      },
      {
        title: "3. Pourquoi nous traitons ces données (finalités et bases légales)",
        blocks: [
          { t: "table",
            head: ["Finalité", "Données concernées", "Base légale (RGPD)"],
            rows: [
              ["Créer et gérer ton compte, te connecter", "E-mail, prénom", "Exécution du contrat (art. 6.1.b)"],
              ["Capturer, stocker et te restituer tes souvenirs", "Texte, audio, photos", "Exécution du contrat (art. 6.1.b)"],
              ["Transcrire ta voix en texte", "Audio", "Exécution du contrat (art. 6.1.b)"],
              ["Analyser et organiser tes souvenirs (émotion, thèmes, recherche)", "Texte, données dérivées", "Exécution du contrat / intérêt légitime (art. 6.1.f)"],
              ["Réécrire un souvenir « au propre » (embellissement)", "Texte", "À ta demande / consentement (art. 6.1.a)"],
              ["Année de naissance, questionnaire, contact pour retour", "Données facultatives", "Consentement (art. 6.1.a)"],
              ["Sécurité, prévention des abus, bon fonctionnement", "Données techniques", "Intérêt légitime (art. 6.1.f)"],
            ] },
          { t: "p", x: "Lorsque le traitement repose sur ton **consentement**, tu peux le retirer à tout moment (voir la section Tes droits), sans que cela remette en cause ce qui a été fait avant." },
        ],
      },
      {
        title: "4. Transparence sur l'analyse automatique (IA)",
        blocks: [
          { t: "p", x: "rememor utilise des traitements automatisés pour rendre le service utile :" },
          { t: "ul", x: [
            "**Transcription** de tes enregistrements vocaux en texte.",
            "**Analyse** du contenu pour en dégager une émotion, des thèmes, une date probable et des points communs entre souvenirs.",
            "**Embellissement** (optionnel, activé par toi) : une réécriture « au propre » de ton texte.",
          ] },
          { t: "p", x: "Ces traitements visent uniquement à **organiser et restituer tes propres souvenirs**. Ils ne servent à aucune décision produisant des effets juridiques à ton égard. **Ton souvenir d'origine (texte et audio) est toujours conservé** : l'embellissement n'est qu'une version supplémentaire, et l'original reste accessible à tout moment." },
          { t: "p", x: "**Les souvenirs que tu marques comme sensibles ne te sont jamais proposés de façon spontanée** : ils ne réapparaissent que si tu les demandes explicitement." },
        ],
      },
      {
        title: "5. Qui a accès à tes données",
        blocks: [
          { t: "p", x: "Tes souvenirs ne sont **pas** rendus visibles aux autres utilisateurs. Seuls interviennent des **prestataires techniques** (sous-traitants), strictement pour faire fonctionner le service :" },
          { t: "ul", x: [
            "**Supabase** — hébergement de la base de données, authentification et stockage des fichiers (audio, photos). Hébergement situé au **Royaume-Uni (région de Londres)**.",
            "**OpenAI** — transcription vocale et analyse / réécriture de texte. Établi aux **États-Unis**.",
            "**Google (Google Workspace)** — envoi des e-mails de connexion.",
            "**n8n** — orchestration de l'analyse automatique des souvenirs.",
            "**Emergent** — hébergement de l'application.",
          ] },
          { t: "p", x: "Ces prestataires n'utilisent tes données que pour la prestation qui leur est confiée, et sont liés par des engagements de confidentialité et de protection des données." },
          { t: "p", x: "Nous pouvons également divulguer des données si la loi l'exige (obligation légale, demande d'une autorité compétente)." },
        ],
      },
      {
        title: "6. Transferts hors de l'Union européenne",
        blocks: [
          { t: "p", x: "L'hébergement principal de tes données (base de données et fichiers) est assuré par **Supabase dans la région de Londres (Royaume-Uni)**. Le Royaume-Uni bénéficie d'une **décision d'adéquation** de la Commission européenne, garantissant un niveau de protection équivalent à celui de l'Union européenne." },
          { t: "p", x: "Certains prestataires sont établis **aux États-Unis** : **OpenAI** (transcription et analyse de texte) et, le cas échéant, **Google** (envoi des e-mails). Les transferts vers ces prestataires sont encadrés par des **garanties appropriées** au sens du RGPD, notamment les **Clauses Contractuelles Types** de la Commission européenne." },
        ],
      },
      {
        title: "7. Combien de temps nous conservons tes données",
        blocks: [
          { t: "ul", x: [
            "Tes souvenirs et les données associées sont conservés **tant que ton compte existe**.",
            "Lorsque tu **supprimes un souvenir**, son texte, son audio et ses photos sont supprimés. Lorsque tu **supprimes ton compte**, l'ensemble de tes données est supprimé.",
            "Des copies de sauvegarde techniques peuvent subsister pour une **durée limitée** avant effacement définitif.",
          ] },
        ],
      },
      {
        title: "8. Sécurité",
        blocks: [
          { t: "p", x: "Nous mettons en œuvre des mesures techniques et organisationnelles raisonnables pour protéger tes données : connexions chiffrées (HTTPS), stockage des fichiers dans des espaces privés, accès restreint par des règles de sécurité au niveau de la base de données (chaque utilisateur ne peut accéder qu'à ses propres souvenirs). Aucun système n'étant infaillible, nous ne pouvons garantir une sécurité absolue." },
        ],
      },
      {
        title: "9. Tes droits",
        blocks: [
          { t: "p", x: "Conformément au RGPD, tu disposes des droits suivants :" },
          { t: "ul", x: [
            "**Accès** : savoir quelles données nous traitons.",
            "**Rectification** : corriger des données inexactes (tu peux notamment modifier tes souvenirs et ton prénom directement dans l'application).",
            "**Effacement** (« droit à l'oubli ») : supprimer tes souvenirs ou ton compte (possible directement dans l'application).",
            "**Portabilité** : récupérer tes souvenirs dans un format réutilisable — un **export de tes données** est disponible dans les réglages de l'application.",
            "**Limitation** et **opposition** au traitement.",
            "**Retrait du consentement** à tout moment, pour les traitements qui en dépendent.",
          ] },
          { t: "p", x: "Pour exercer ces droits, écris-nous à **eric@0gbaltics.com**. Tu as aussi le droit d'introduire une réclamation auprès de l'autorité de contrôle compétente — en Lettonie, la **Datu valsts inspekcija** (DVI) — ou auprès de l'autorité de ton pays de résidence." },
        ],
      },
      {
        title: "10. Pas de publicité, pas de revente",
        blocks: [
          { t: "p", x: "Nous **ne vendons pas** tes données. Nous ne les utilisons pas à des fins publicitaires, et aucun annonceur ne paie pour apparaître dans l'application. rememor ne se finance pas avec tes souvenirs." },
        ],
      },
      {
        title: "11. Mineurs",
        blocks: [
          { t: "p", x: "rememor n'est pas destiné aux personnes de moins de **16 ans** (ou de l'âge du consentement numérique applicable dans ton pays de résidence). Si tu es responsable légal et que tu constates qu'un mineur nous a transmis des données sans autorisation, contacte-nous afin que nous les supprimions." },
        ],
      },
      {
        title: "12. Stockage local sur ton appareil",
        blocks: [
          { t: "p", x: "Pour fonctionner (rester connecté, mémoriser tes préférences comme la langue ou le thème), l'application stocke certaines informations **localement sur ton appareil**. Nous n'utilisons pas de cookies à des fins publicitaires ou de suivi tiers." },
        ],
      },
      {
        title: "13. Modifications de cette politique",
        blocks: [
          { t: "p", x: "Nous pouvons faire évoluer cette politique. En cas de changement important, nous t'en informerons par un moyen approprié. La date de dernière mise à jour figure en haut de cette page." },
        ],
      },
      {
        title: "14. Contact",
        blocks: [
          { t: "p", x: "Pour toute question relative à cette politique ou à tes données : **eric@0gbaltics.com**" },
        ],
      },
    ],
  },

  en: {
    title: "Privacy Policy — rememor",
    updated: "Last updated: 30 June 2026",
    back: "Back to rememor",
    intro: [
      "rememor is an app that lets you capture your memories — by voice or in writing — and find them again over time. Your memories are what's most personal to you. This page explains, in plain language, what data we process, why, with whom, and what your rights are.",
      "One principle guides everything else: **your memories belong to you. We do not sell them, we do not exploit them for any advertising purpose, and we do not share them with anyone other than the technical providers strictly necessary to run the service.**",
    ],
    sections: [
      {
        title: "1. Who is responsible for your data",
        blocks: [
          { t: "p", x: "The data controller is:" },
          { t: "ul", x: ["**Eric Brisset**", "**Latvia**", "Contact: **eric@0gbaltics.com**"] },
          { t: "p", x: "For any question about your data or to exercise your rights, you can write to us at the address above." },
        ],
      },
      {
        title: "2. What data we process",
        blocks: [
          { t: "h3", x: "Account data" },
          { t: "ul", x: [
            "Your **email address** (used to sign you in via a secure link).",
            "Your **first name** (to personalise the app).",
            "Your **year of birth** *(optional — to better place your memories in time)*.",
          ] },
          { t: "h3", x: "Your memories (the heart of the service)" },
          { t: "ul", x: [
            "The **text** of your memories (typed or transcribed from your voice).",
            "The **audio recordings** you make.",
            "The **photos** you choose to attach to a memory.",
          ] },
          { t: "h3", x: "Data generated by automatic analysis" },
          { t: "p", x: "To help you find and organise your memories, they are analysed automatically, which produces: a dominant **emotion**, **themes** (life period, place, people mentioned…), a possible **date** for the memory, and a **numerical representation** (an “embedding”) used for search and for connecting memories together." },
          { t: "h3", x: "Optional responses" },
          { t: "ul", x: [
            "If you wish, your answers to the short “Help us improve rememor” survey (habits, expectations…).",
            "Your possible consent to be contacted about the service.",
          ] },
          { t: "h3", x: "Minimal technical data" },
          { t: "ul", x: [
            "Basic information needed to operate (device/browser type, language, timestamps of actions). We do not carry out advertising profiling.",
          ] },
          { t: "note", x: "**Important:** you freely decide what you record. A memory may contain sensitive information about you or about third parties. Be mindful of what you entrust to the app, and only record information about other people in a way that respects their privacy." },
        ],
      },
      {
        title: "3. Why we process this data (purposes and legal bases)",
        blocks: [
          { t: "table",
            head: ["Purpose", "Data concerned", "Legal basis (GDPR)"],
            rows: [
              ["Create and manage your account, sign you in", "Email, first name", "Performance of the contract (art. 6.1.b)"],
              ["Capture, store and return your memories", "Text, audio, photos", "Performance of the contract (art. 6.1.b)"],
              ["Transcribe your voice into text", "Audio", "Performance of the contract (art. 6.1.b)"],
              ["Analyse and organise your memories (emotion, themes, search)", "Text, derived data", "Performance of the contract / legitimate interest (art. 6.1.f)"],
              ["Rewrite a memory “neatly” (embellishment)", "Text", "At your request / consent (art. 6.1.a)"],
              ["Year of birth, survey, contact for feedback", "Optional data", "Consent (art. 6.1.a)"],
              ["Security, abuse prevention, proper operation", "Technical data", "Legitimate interest (art. 6.1.f)"],
            ] },
          { t: "p", x: "Where processing is based on your **consent**, you can withdraw it at any time (see the Your rights section), without affecting what was done beforehand." },
        ],
      },
      {
        title: "4. Transparency about automatic analysis (AI)",
        blocks: [
          { t: "p", x: "rememor uses automated processing to make the service useful:" },
          { t: "ul", x: [
            "**Transcription** of your voice recordings into text.",
            "**Analysis** of the content to derive an emotion, themes, a probable date and commonalities between memories.",
            "**Embellishment** (optional, enabled by you): a “neat” rewrite of your text.",
          ] },
          { t: "p", x: "This processing is solely intended to **organise and return your own memories**. It is not used for any decision producing legal effects concerning you. **Your original memory (text and audio) is always kept**: the embellishment is only an additional version, and the original remains accessible at all times." },
          { t: "p", x: "**Memories you mark as sensitive are never shown to you spontaneously**: they only reappear if you explicitly ask for them." },
        ],
      },
      {
        title: "5. Who has access to your data",
        blocks: [
          { t: "p", x: "Your memories are **not** made visible to other users. Only **technical providers** (processors) are involved, strictly to operate the service:" },
          { t: "ul", x: [
            "**Supabase** — hosting of the database, authentication and file storage (audio, photos). Hosting located in the **United Kingdom (London region)**.",
            "**OpenAI** — voice transcription and text analysis / rewriting. Established in the **United States**.",
            "**Google (Google Workspace)** — sending sign-in emails.",
            "**n8n** — orchestration of the automatic analysis of memories.",
            "**Emergent** — hosting of the application.",
          ] },
          { t: "p", x: "These providers only use your data for the service entrusted to them, and are bound by confidentiality and data-protection commitments." },
          { t: "p", x: "We may also disclose data where required by law (legal obligation, request from a competent authority)." },
        ],
      },
      {
        title: "6. Transfers outside the European Union",
        blocks: [
          { t: "p", x: "The primary hosting of your data (database and files) is provided by **Supabase in the London region (United Kingdom)**. The United Kingdom benefits from an **adequacy decision** of the European Commission, guaranteeing a level of protection equivalent to that of the European Union." },
          { t: "p", x: "Some providers are established **in the United States**: **OpenAI** (transcription and text analysis) and, where applicable, **Google** (sending emails). Transfers to these providers are framed by **appropriate safeguards** within the meaning of the GDPR, in particular the European Commission's **Standard Contractual Clauses**." },
        ],
      },
      {
        title: "7. How long we keep your data",
        blocks: [
          { t: "ul", x: [
            "Your memories and associated data are kept **as long as your account exists**.",
            "When you **delete a memory**, its text, audio and photos are deleted. When you **delete your account**, all of your data is deleted.",
            "Technical backup copies may remain for a **limited time** before final erasure.",
          ] },
        ],
      },
      {
        title: "8. Security",
        blocks: [
          { t: "p", x: "We implement reasonable technical and organisational measures to protect your data: encrypted connections (HTTPS), storage of files in private spaces, restricted access through security rules at the database level (each user can only access their own memories). As no system is infallible, we cannot guarantee absolute security." },
        ],
      },
      {
        title: "9. Your rights",
        blocks: [
          { t: "p", x: "In accordance with the GDPR, you have the following rights:" },
          { t: "ul", x: [
            "**Access**: to know what data we process.",
            "**Rectification**: to correct inaccurate data (you can in particular edit your memories and your first name directly in the app).",
            "**Erasure** (the “right to be forgotten”): to delete your memories or your account (possible directly in the app).",
            "**Portability**: to retrieve your memories in a reusable format — a **data export** is available in the app's settings.",
            "**Restriction** and **objection** to processing.",
            "**Withdrawal of consent** at any time, for processing that depends on it.",
          ] },
          { t: "p", x: "To exercise these rights, write to us at **eric@0gbaltics.com**. You also have the right to lodge a complaint with the competent supervisory authority — in Latvia, the **Datu valsts inspekcija** (DVI) — or with the authority of your country of residence." },
        ],
      },
      {
        title: "10. No advertising, no resale",
        blocks: [
          { t: "p", x: "We **do not sell** your data. We do not use it for advertising purposes, and no advertiser pays to appear in the app. rememor is not funded with your memories." },
        ],
      },
      {
        title: "11. Minors",
        blocks: [
          { t: "p", x: "rememor is not intended for people under **16** (or the age of digital consent applicable in your country of residence). If you are a legal guardian and find that a minor has sent us data without authorisation, contact us so that we can delete it." },
        ],
      },
      {
        title: "12. Local storage on your device",
        blocks: [
          { t: "p", x: "To work (keeping you signed in, remembering your preferences such as language or theme), the app stores certain information **locally on your device**. We do not use cookies for advertising or third-party tracking purposes." },
        ],
      },
      {
        title: "13. Changes to this policy",
        blocks: [
          { t: "p", x: "We may update this policy. In the event of a significant change, we will inform you by an appropriate means. The last-updated date appears at the top of this page." },
        ],
      },
      {
        title: "14. Contact",
        blocks: [
          { t: "p", x: "For any question about this policy or your data: **eric@0gbaltics.com**" },
        ],
      },
    ],
  },

  ru: {
    title: "Политика конфиденциальности — rememor",
    updated: "Последнее обновление: 30 июня 2026 г.",
    back: "Назад в rememor",
    intro: [
      "rememor — это приложение, которое позволяет фиксировать ваши воспоминания — голосом или письменно — и находить их со временем. Ваши воспоминания — это самое личное, что у вас есть. На этой странице понятным языком объясняется, какие данные мы обрабатываем, зачем, с кем и каковы ваши права.",
      "Один принцип определяет всё остальное: **ваши воспоминания принадлежат вам. Мы не продаём их, не используем их в рекламных целях и не передаём их никому, кроме технических поставщиков, строго необходимых для работы сервиса.**",
    ],
    sections: [
      {
        title: "1. Кто отвечает за ваши данные",
        blocks: [
          { t: "p", x: "Контролёром данных является:" },
          { t: "ul", x: ["**Eric Brisset**", "**Латвия**", "Контакт: **eric@0gbaltics.com**"] },
          { t: "p", x: "По любым вопросам о ваших данных или для реализации ваших прав вы можете написать нам по указанному выше адресу." },
        ],
      },
      {
        title: "2. Какие данные мы обрабатываем",
        blocks: [
          { t: "h3", x: "Данные учётной записи" },
          { t: "ul", x: [
            "Ваш **адрес электронной почты** (используется для входа по защищённой ссылке).",
            "Ваше **имя** (для персонализации приложения).",
            "Ваш **год рождения** *(необязательно — чтобы лучше располагать воспоминания во времени)*.",
          ] },
          { t: "h3", x: "Ваши воспоминания (суть сервиса)" },
          { t: "ul", x: [
            "**Текст** ваших воспоминаний (введённый или полученный из расшифровки вашего голоса).",
            "**Аудиозаписи**, которые вы делаете.",
            "**Фотографии**, которые вы решаете прикрепить к воспоминанию.",
          ] },
          { t: "h3", x: "Данные, создаваемые автоматическим анализом" },
          { t: "p", x: "Чтобы помочь вам находить и упорядочивать воспоминания, они анализируются автоматически, что даёт: доминирующую **эмоцию**, **темы** (период жизни, место, упомянутые люди…), возможную **дату** воспоминания и **числовое представление** («эмбеддинг»), используемое для поиска и сопоставления воспоминаний." },
          { t: "h3", x: "Необязательные ответы" },
          { t: "ul", x: [
            "При желании — ваши ответы на короткий опрос «Помогите нам улучшить rememor» (привычки, ожидания…).",
            "Ваше возможное согласие на то, чтобы с вами связались по поводу сервиса.",
          ] },
          { t: "h3", x: "Минимальные технические данные" },
          { t: "ul", x: [
            "Базовая информация, необходимая для работы (тип устройства/браузера, язык, отметки времени действий). Мы не осуществляем рекламное профилирование.",
          ] },
          { t: "note", x: "**Важно:** вы сами свободно решаете, что записывать. Воспоминание может содержать конфиденциальную информацию о вас или о третьих лицах. Будьте внимательны к тому, что вы доверяете приложению, и записывайте информацию о других людях только с уважением к их частной жизни." },
        ],
      },
      {
        title: "3. Зачем мы обрабатываем эти данные (цели и правовые основания)",
        blocks: [
          { t: "table",
            head: ["Цель", "Затрагиваемые данные", "Правовое основание (GDPR)"],
            rows: [
              ["Создание и управление учётной записью, вход", "E-mail, имя", "Исполнение договора (ст. 6.1.b)"],
              ["Фиксация, хранение и выдача ваших воспоминаний", "Текст, аудио, фото", "Исполнение договора (ст. 6.1.b)"],
              ["Расшифровка вашего голоса в текст", "Аудио", "Исполнение договора (ст. 6.1.b)"],
              ["Анализ и упорядочивание воспоминаний (эмоция, темы, поиск)", "Текст, производные данные", "Исполнение договора / законный интерес (ст. 6.1.f)"],
              ["Переписать воспоминание «начисто» (украшение)", "Текст", "По вашему запросу / согласие (ст. 6.1.a)"],
              ["Год рождения, опрос, контакт для обратной связи", "Необязательные данные", "Согласие (ст. 6.1.a)"],
              ["Безопасность, предотвращение злоупотреблений, работа сервиса", "Технические данные", "Законный интерес (ст. 6.1.f)"],
            ] },
          { t: "p", x: "Когда обработка основана на вашем **согласии**, вы можете отозвать его в любой момент (см. раздел «Ваши права»), без ущерба для того, что было сделано ранее." },
        ],
      },
      {
        title: "4. Прозрачность автоматического анализа (ИИ)",
        blocks: [
          { t: "p", x: "rememor использует автоматизированную обработку, чтобы сделать сервис полезным:" },
          { t: "ul", x: [
            "**Расшифровка** ваших голосовых записей в текст.",
            "**Анализ** содержания для выделения эмоции, тем, вероятной даты и общих черт между воспоминаниями.",
            "**Украшение** (необязательно, включается вами): «чистая» переработка вашего текста.",
          ] },
          { t: "p", x: "Эта обработка предназначена исключительно для того, чтобы **упорядочивать и возвращать ваши собственные воспоминания**. Она не используется для принятия решений, имеющих для вас юридические последствия. **Ваше исходное воспоминание (текст и аудио) всегда сохраняется**: украшение — это лишь дополнительная версия, а оригинал остаётся доступным в любой момент." },
          { t: "p", x: "**Воспоминания, которые вы помечаете как конфиденциальные, никогда не показываются вам спонтанно**: они появляются снова только если вы явно их запросите." },
        ],
      },
      {
        title: "5. Кто имеет доступ к вашим данным",
        blocks: [
          { t: "p", x: "Ваши воспоминания **не** видны другим пользователям. Задействованы только **технические поставщики** (обработчики), строго для работы сервиса:" },
          { t: "ul", x: [
            "**Supabase** — хостинг базы данных, аутентификация и хранение файлов (аудио, фото). Хостинг находится в **Великобритании (регион Лондон)**.",
            "**OpenAI** — расшифровка голоса и анализ / переработка текста. Зарегистрирован в **США**.",
            "**Google (Google Workspace)** — отправка писем для входа.",
            "**n8n** — оркестрация автоматического анализа воспоминаний.",
            "**Emergent** — хостинг приложения.",
          ] },
          { t: "p", x: "Эти поставщики используют ваши данные только для порученной им услуги и связаны обязательствами по конфиденциальности и защите данных." },
          { t: "p", x: "Мы также можем раскрывать данные, если этого требует закон (юридическая обязанность, запрос компетентного органа)." },
        ],
      },
      {
        title: "6. Передача за пределы Европейского союза",
        blocks: [
          { t: "p", x: "Основной хостинг ваших данных (база данных и файлы) обеспечивается **Supabase в регионе Лондон (Великобритания)**. Великобритания пользуется **решением об адекватности** Европейской комиссии, гарантирующим уровень защиты, эквивалентный уровню Европейского союза." },
          { t: "p", x: "Некоторые поставщики зарегистрированы **в США**: **OpenAI** (расшифровка и анализ текста) и, при необходимости, **Google** (отправка писем). Передача данных этим поставщикам обеспечивается **надлежащими гарантиями** по смыслу GDPR, в частности **Стандартными договорными положениями** Европейской комиссии." },
        ],
      },
      {
        title: "7. Сколько времени мы храним ваши данные",
        blocks: [
          { t: "ul", x: [
            "Ваши воспоминания и связанные данные хранятся **пока существует ваша учётная запись**.",
            "Когда вы **удаляете воспоминание**, его текст, аудио и фотографии удаляются. Когда вы **удаляете свою учётную запись**, все ваши данные удаляются.",
            "Технические резервные копии могут сохраняться в течение **ограниченного времени** до окончательного удаления.",
          ] },
        ],
      },
      {
        title: "8. Безопасность",
        blocks: [
          { t: "p", x: "Мы применяем разумные технические и организационные меры для защиты ваших данных: зашифрованные соединения (HTTPS), хранение файлов в приватных пространствах, ограниченный доступ через правила безопасности на уровне базы данных (каждый пользователь может получить доступ только к своим воспоминаниям). Поскольку ни одна система не является безупречной, мы не можем гарантировать абсолютную безопасность." },
        ],
      },
      {
        title: "9. Ваши права",
        blocks: [
          { t: "p", x: "В соответствии с GDPR вы обладаете следующими правами:" },
          { t: "ul", x: [
            "**Доступ**: знать, какие данные мы обрабатываем.",
            "**Исправление**: исправлять неточные данные (в частности, вы можете редактировать свои воспоминания и имя прямо в приложении).",
            "**Удаление** («право быть забытым»): удалять свои воспоминания или учётную запись (возможно прямо в приложении).",
            "**Переносимость**: получить свои воспоминания в пригодном для повторного использования формате — **экспорт данных** доступен в настройках приложения.",
            "**Ограничение** и **возражение** против обработки.",
            "**Отзыв согласия** в любой момент для обработки, которая от него зависит.",
          ] },
          { t: "p", x: "Чтобы реализовать эти права, напишите нам на **eric@0gbaltics.com**. Вы также вправе подать жалобу в компетентный надзорный орган — в Латвии это **Datu valsts inspekcija** (DVI) — либо в орган страны вашего проживания." },
        ],
      },
      {
        title: "10. Без рекламы, без перепродажи",
        blocks: [
          { t: "p", x: "Мы **не продаём** ваши данные. Мы не используем их в рекламных целях, и ни один рекламодатель не платит за появление в приложении. rememor не финансируется за счёт ваших воспоминаний." },
        ],
      },
      {
        title: "11. Несовершеннолетние",
        blocks: [
          { t: "p", x: "rememor не предназначен для лиц младше **16 лет** (или возраста цифрового согласия, применимого в стране вашего проживания). Если вы законный представитель и обнаружили, что несовершеннолетний передал нам данные без разрешения, свяжитесь с нами, чтобы мы их удалили." },
        ],
      },
      {
        title: "12. Локальное хранение на вашем устройстве",
        blocks: [
          { t: "p", x: "Для работы (чтобы вы оставались в системе, для запоминания предпочтений, таких как язык или тема) приложение хранит некоторую информацию **локально на вашем устройстве**. Мы не используем cookie в рекламных целях или для стороннего отслеживания." },
        ],
      },
      {
        title: "13. Изменения этой политики",
        blocks: [
          { t: "p", x: "Мы можем обновлять эту политику. В случае существенного изменения мы уведомим вас подходящим способом. Дата последнего обновления указана вверху этой страницы." },
        ],
      },
      {
        title: "14. Контакты",
        blocks: [
          { t: "p", x: "По любым вопросам об этой политике или ваших данных: **eric@0gbaltics.com**" },
        ],
      },
    ],
  },
};
